import React from 'react';
import CustomScrollTrigger from 'js/wrapper/CustomScrollTrigger';
import { Parallax } from 'react-scroll-parallax';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useAbstractCmsElement } from 'components/cms/hooks/useAbstractCmsElementNew';

import Loader from 'components/layout/Loader';

const CmsElementUploadedVideo = (props) => {
  const {
    editMode,
    index,
    showElementSettings,
  } = props;

  // CMS logic hook (handles state, params, and styles)
  const {
    shown,
    setShown,
    getMainId,
    getCustomId,
    getParamValue,
    getBackgroundStyle,
    elementStyleTag,
    getParallaxData,
    getAppendHtml,
    cssClasses,
    responsiveClasses
  } = useAbstractCmsElement(props);

  // dnd-kit sortable hook
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: index,
    disabled: !editMode
  });

  // Apply dnd-kit transforms
  const dndStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    position: 'relative',
    cursor: editMode ? 'move' : 'default',
    width: '100%',
    minHeight: '20px' // Ensures visibility during layout shifts
  };

  // Video logic
  const isVideoSelected = !!getParamValue('videoFile');
  const isUploaded = getParamValue('videoUploaded') === '1';
  const thumbUrl = getParamValue('videoThumbUrl');
  const imageUrl = getParamValue('imageUrl', '');
  const aspectRatio = getParamValue('videoAspectRatio', '16-9');

  // Visibility: In edit mode we always show the element, otherwise wait for scroll
  const isVisible = editMode || shown;
  const shownClass = isVisible ? 'elem-shown' : 'elem-hidden';
  const wrapperClass = `ms-uploaded-video-wrapper ms-uploaded-video-wrapper-${aspectRatio}`;

  const innerContent = (
    <div
      id={getMainId()}
      className={`${wrapperClass} ${shownClass} ${cssClasses} ${responsiveClasses}`.trim()}
      onClick={(e) => editMode && showElementSettings(e, index)}
      style={{
        ...getBackgroundStyle(),
        position: 'relative'
      }}
    >
      {elementStyleTag}

      <div className="ms-uploaded-video-player-content">
        {isVideoSelected && isUploaded ? (
          <video
            autoPlay
            loop
            controls
            poster={imageUrl || undefined}
            key={thumbUrl} // Forces re-render if URL changes
            style={{ width: '100%', height: 'auto', display: 'block' }}
          >
            <source src={thumbUrl} type="video/mp4" />
          </video>
        ) : (
          <div className="p-t-15 p-b-15 text-center bg-master-lightest">
            {isVideoSelected && !isUploaded ? (
              <Loader />
            ) : (
              <i className="fas fa-play fa-4x" style={{ verticalAlign: 'middle' }} />
            )}
          </div>
        )}
      </div>

      {getAppendHtml()}
    </div>
  );

  const parallaxData = getParallaxData();

  return (
    <div
      ref={setNodeRef}
      style={dndStyle}
      {...attributes}
      {...listeners}
      className="cms-element-sortable-wrapper"
    >
      <CustomScrollTrigger className="cms-overflow-fix" onEnter={() => setShown(true)} {...getCustomId()}>
        {parallaxData ? (
          <Parallax {...parallaxData}>
            {innerContent}
          </Parallax>
        ) : (
          innerContent
        )}
      </CustomScrollTrigger>

    </div>
  );
};

export default CmsElementUploadedVideo;
